import { WATCHED_CRONS } from '../types';
import { CRON_SCHEDULES, nextTickHint } from '../cronSchedules';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface CronHeartbeatStripProps {
  /** Map from cron name → last-complete ISO timestamp. */
  heartbeats: Record<string, string> | undefined;
  /** ms age over which a cron is considered stale. Default 30 minutes. */
  staleAfterMs?: number;
  /** ms age over which a cron is considered silent/critical. Default 2 hours. */
  silentAfterMs?: number;
}

export function CronHeartbeatStrip({
  heartbeats,
  staleAfterMs = 30 * 60_000,
  silentAfterMs = 2 * 60 * 60_000,
}: CronHeartbeatStripProps) {
  const now = Date.now();
  return (
    <ul className="flex flex-wrap gap-2">
      {WATCHED_CRONS.map((cron) => {
        const ts = heartbeats?.[cron];
        const ageMs = ts ? Math.max(0, now - new Date(ts).getTime()) : null;
        const state = ageMs === null
          ? 'unknown'
          : ageMs > silentAfterMs
            ? 'silent'
            : ageMs > staleAfterMs
              ? 'stale'
              : 'healthy';
        const schedule = CRON_SCHEDULES[cron];
        const nextHint = nextTickHint(schedule?.cadenceMinutes);
        return (
          <li key={cron}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  role="status"
                  aria-label={`${cron} ${state}`}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
                    state === 'healthy'
                      ? 'border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'
                      : state === 'stale'
                        ? 'border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]'
                        : state === 'silent'
                          ? 'border-destructive/40 bg-destructive/10 text-destructive'
                          : 'border-dashed text-muted-foreground',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'inline-block h-2 w-2 rounded-full',
                      state === 'healthy' && 'bg-[hsl(var(--success))]',
                      state === 'stale' && 'bg-[hsl(var(--warning))]',
                      state === 'silent' && 'bg-destructive',
                      state === 'unknown' && 'bg-muted-foreground',
                    )}
                  />
                  <span className="font-medium">{cron.replace(/Cron$/, '')}</span>
                  {schedule && (
                    <span className="text-[10px] opacity-75">· {schedule.label}</span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {schedule && (
                  <>
                    Schedule: {schedule.label}
                    <br />
                  </>
                )}
                {ts ? (
                  <>
                    Last complete: <span className="font-mono">{ts}</span>
                    <br />
                    {humanizeAge(ageMs as number)} ago · {state}
                    {nextHint && (
                      <>
                        <br />
                        {nextHint}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    No heartbeat yet · {state}
                    {nextHint && (
                      <>
                        <br />
                        {nextHint}
                      </>
                    )}
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          </li>
        );
      })}
    </ul>
  );
}

function humanizeAge(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
